import React, {useState, useEffect, FunctionComponent} from "react";
import {
    Grid,
    Box,
    Typography,
    Container,
    Button,
    TextField,
    Checkbox,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import Rating from "../../Components/Rating";
import {useHistory, useLocation} from "react-router-dom";
import API from "../../helpers/apiHelpers";
import {userRole} from "../../interfaces";
import AppointmentsReviewNavigation from "../../Components/Navigation/AppointmentsReviewNavigation";
import {useSnackbar} from "notistack";
import {driversForOwner, driversForVet, reasonsForRatingOwner} from "../../Constants/driversForRating";
import DocCard from "../../Components/DocCard";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {setOwnerReview, setProviderReview} from "../../redux/appointment/appointment.actions";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Switch from "react-switch";
import {
    colorInputBorder,
    colorPrimary,
    colorWhite
} from "../../Constants/theme";
import GoogleReview from "./GoogleReview";

interface AppointmentRateProps {
    userType: userRole
}

const useStyle = makeStyles((theme) => ({
    wrapper: {
        marginTop: 90,
        marginBottom: 50,
    },
    leftContentWrapper: {
        width: "50%",
        height: "80%",
        marginTop: "20px",
        [theme.breakpoints.up("md")]: {
            marginLeft: "20px",
            marginTop: "0",
        },
    },
    bullet: {
        display: "inline-block",
        margin: "0 2px",
        transform: "scale(0.8)",
    },
    title: {
        fontSize: 14,
    },
    pos: {
        marginBottom: 12,
    },
    cardContent: {
        display: "flex",
        flexDirection: "column",
        width: "260px",
        height: "425px",
        alignItems: "center",
        justifyContent: "space-between"
    },
    docPhoto: {
        display: "flex",
        justifyContent: "center"
    },
    devideBorrder: {
        border: "1px solid #CCCDE5",
        width: "100%"
    },
}));

const AppointmentRate: FunctionComponent<AppointmentRateProps> = ({userType = "VET"}: AppointmentRateProps) => {
    const classes = useStyle();
    const history = useHistory();
    const [diagnos, setDiagnos] = useState("");
    const [diagnosisOther, setDiagnosisOther] = useState("");
    const [docLastName, setDocLastName] = useState("");
    const [docFirstName, setDocFirstName] = useState("");
    const [docEdu, setDocEdu] = useState("");
    const [docImage, setDocImage] = useState("");
    const [docType, setDocType] = useState("");
    const [scheduledDate, setScheduledDate] = useState("");
    const [appointmentType, setAppointmentType] = useState("CHAT_ONLY");
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [isResultPrescription, setIsResultPrescription] = useState<undefined | boolean>();
    const [recommendedOTC, setRecommendedOTC] = useState<undefined | boolean>();
    const [talkedAboutPartnersProduct, setTalkedAboutPartnersProduct] = useState(false);
    const [isAvailableForMarketing, setIsAvailableForMarketing] = useState(true);
    const [whyHelloRalphie, setWhyHelloRalphie] = useState(null);
    const [otherReason, setOtherReason] = useState("");
    const [googleReviewOpen, setGoogleReviewOpen] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const user = useSelector((state: RootState) => state?.user);
    const appointment = useSelector((state: RootState) => state?.appointment.appointment);
    const dispatch = useAppDispatch();
    useEffect(() => {
        setScheduledDate(appointment.scheduledDate);
        setAppointmentType(appointment.type);
        API.getProfiles({userId: appointment.vetUserId})
            .then((profile) => {
                const {type, firstname, lastName, education, image} = profile[0];
                setDocFirstName(firstname || "");
                setDocLastName(lastName || "");
                setDocEdu(education || "");
                setDocImage(image || "");
                setDocType(type || "");
            });
    }, []);

    const handleSubmit = async () => {
        let moveForward = true;
        if (diagnos === "other" && !diagnosisOther.length) {
            enqueueSnackbar("Please specify issues discussed!", {variant: "error"});
            moveForward = false;
        }
        if (user.role === "OWNER" && !isWhiteLabel){
            if (!whyHelloRalphie || (whyHelloRalphie === "Other" && !otherReason.length)) {
                enqueueSnackbar("Please describe why did you choose The Video Vets!", {variant: "error"});
                moveForward = false;
            }
        }
        if (user.role === "VET") {
            if (recommendedOTC === undefined) {
                enqueueSnackbar("Please specify recommended OTC medication!", {variant: "error"});
                moveForward = false;
            }
            if (isResultPrescription === undefined) {
                enqueueSnackbar("Please specify if the appointment resulted in a prescription!", {variant: "error"});
                moveForward = false;
            }
        }
        if (moveForward) {
            let review = {};
            switch (user.role) {
                case "OWNER":
                    review = {
                        appointmentId: appointment.id,
                        stars: rating,
                        allowsMarketing: isAvailableForMarketing,
                        comments: comment,
                        requestsPrescription: isResultPrescription,
                        type: "owner",
                    };
                    if (whyHelloRalphie === "Other" && otherReason.length) {
                        Object.assign(review, {whyHelloRalphie: otherReason});
                    } else {
                        Object.assign(review, {whyHelloRalphie});
                    }
                    await dispatch(setOwnerReview(review));
                    if (rating === 5 && !isWhiteLabel) {
                        setGoogleReviewOpen(true);
                    } else {
                        history.push("/appointments");
                    }
                    break;
                case "VET":
                    review = {
                        appointmentId: appointment.id,
                        stars: rating,
                        comments: comment,
                        requestsPrescription: isResultPrescription,
                        type: "vet",
                        isNewClient: false,
                        diagnosis: "other",
                        diagnosisOther: "outdated",
                        recommendedOTC,
                        recommendedInPerson: false,
                    };
                    if(isWhiteLabel) Object.assign(review, {didYouTalkAboutThePartnerProduct: talkedAboutPartnersProduct});
                    await dispatch(setProviderReview(review));
                    history.push({pathname: "/apointmentNotes", state: {appointmentId: appointment.id}});
                    break;
                default:
                    break;
            }
        }
    };
    return (
        googleReviewOpen ?
            <GoogleReview />
            :
            <Box className={classes.wrapper}>
                <Container maxWidth="lg">
                    <Grid container spacing={6}>
                        <Grid item sm={4} xs={12} justify="center">
                            <DocCard docType={docType} docFirstName={docFirstName} docLastName={docLastName} docEdu={docEdu}
                                docImage={docImage} setDocImage={setDocImage} scheduledDate={scheduledDate}
                                appointmentType={appointmentType} animalName={appointment?.animal?.name}
                                dateOfBirth={appointment?.animal?.dateOfBirth}
                            />
                        </Grid>
                        <Grid item sm={8} xs={12}>
                            <Grid container>
                                <Grid item sm={12} xs={12}>
                                    <Box marginBottom="33px">
                                        <Typography variant="h2">
                                        Rate your experience!
                                        </Typography>
                                    </Box>
                                </Grid>
                                {user.role === "VET" &&
                            <AppointmentsReviewNavigation/>
                                }
                                <Grid item sm={12} xs={12}>
                                    <Box marginBottom="24px">
                                        <Typography variant="h4">
                                        How was your appointment?
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Box marginBottom="24px">
                                    <Rating initialValue={rating} onChange={(rating) => {
                                        setRating(rating);
                                    }}/>
                                </Box>
                                <Grid item sm={12} xs={12}>
                                    <Box marginTop="24px" marginBottom="24px">
                                        <Typography variant="h4">
										What&apos;s the main driver of this rating?
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item sm={12} xs={12}>
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        <TextField
                                            multiline
                                            rows={8}
                                            fullWidth
                                            placeholder="Tell us more!"
                                            variant="outlined"
                                            onChange={(e) => {
                                                setComment(e.target.value);
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                {
                                    user.role === "VET" &&
                                <>
                                	<Grid item sm={12} xs={12}>
                                		<Box marginTop="48px" marginBottom="24px">
                                			<Typography variant="h4">
                                                Recommended OTC medication
                                			</Typography>
                                		</Box>
                                	</Grid>
                                	<Grid item sm={12} xs={12}>
                                		<Box display="flex" margin="10px">
                                			<FormControl component="fieldset" defaultValue={"No"}>
                                				<RadioGroup row aria-label="position" name="position" defaultValue="">
                                					<Box marginRight="70px">
                                						<FormControlLabel
                                							control={<Radio color="primary"/>}
                                							label="Yes"
                                							value={"Yes"}
                                							onClick={() => {
                                								setRecommendedOTC(true);
                                							}}
                                						/>
                                					</Box>
                                					<FormControlLabel
                                						control={<Radio color="primary"/>}
                                						label="No"
                                						value={"No"}
                                						onClick={() => {
                                							setRecommendedOTC(false);
                                						}}
                                					/>
                                				</RadioGroup>
                                			</FormControl>
                                		</Box>
                                	</Grid>
                                	<Grid item sm={12} xs={12}>
                                		<Box marginTop="48px" marginBottom="24px">
                                			<Typography variant="h4">
												Did the appointment result in a prescription?
                                			</Typography>
                                		</Box>
                                	</Grid>
                                	<Grid item sm={12} xs={12}>
                                		<Box display="flex" margin="10px">
                                			<FormControl component="fieldset" defaultValue={"No"}>
                                				<RadioGroup row aria-label="position" name="position" defaultValue="top">
                                					<Box marginRight="70px">
                                						<FormControlLabel
                                							control={<Radio color="primary"/>}
                                							label="Yes"
                                							value={"Yes"}
                                							onClick={() => {
                                								setIsResultPrescription(true);
                                							}}
                                						/>
                                					</Box>
                                					<FormControlLabel
                                						control={<Radio color="primary"/>}
                                						label="No"
                                						value={"No"}
                                						onClick={() => {
                                							setIsResultPrescription(false);
                                						}}
                                					/>
                                				</RadioGroup>
                                			</FormControl>
                                		</Box>
                                	</Grid>
                                </>
                                }
                                {	(user.role === "OWNER" && !isWhiteLabel) &&
								<>
								    <Grid item sm={12} xs={12}>
								        <Box marginTop="24px" marginBottom="24px">
								            <Typography variant="h4">
												Why did you choose The Video Vets?
								            </Typography>
								        </Box>
								        <Autocomplete
								            value={whyHelloRalphie}
								            onChange={(event, value) => {
								                setWhyHelloRalphie(value);
								            }}
								            options={reasonsForRatingOwner}
								            getOptionLabel={(option) => option}
								            style={{width: "100%"}}
								            renderInput={(params) => <TextField {...params} variant="outlined"/>}
								        />
								    </Grid>
								    {	whyHelloRalphie === "Other" &&
										<Grid item sm={12} xs={12}>
										    <Box marginTop="24px" marginBottom="24px">
										        <Typography variant="h4">
											Other Reason
										        </Typography>
										    </Box>
										    <TextField
										        multiline
										        value={otherReason}
										        onChange={(e) => setOtherReason(e.target.value)}
										        variant="outlined"
										        placeholder="Please explain your reason for choosing The Video Vets"
										        style={{width: "100%"}}
										    />
										</Grid>
								    }
								</>
                                }
                                { !isWhiteLabel &&
                                    <Grid item sm={12} xs={12}>
                                        <Box display="flex" marginTop="24px">
                                            <Switch
                                                onChange={(checked) => {
                                                    setIsAvailableForMarketing(checked);
                                                }}
                                                checked={isAvailableForMarketing}
                                                offColor={colorInputBorder}
                                                onColor={colorPrimary}
                                                activeBoxShadow={`0 0 2px 3px ${colorPrimary}`}
                                                uncheckedIcon={<div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        height: "100%",
                                                        fontSize: 14,
                                                        color: colorWhite,
                                                        paddingRight: 2
                                                    }}
                                                >
                                                    No
                                                </div>}
                                                checkedIcon={<div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        height: "100%",
                                                        fontSize: 14,
                                                        color: colorWhite,
                                                        paddingLeft: 2
                                                    }}
                                                >
                                                    Yes
                                                </div>}
                                            />
                                            <Typography style={{marginLeft: 10}}>This review may be used for marketing
                                                purposes</Typography>
                                        </Box>
                                    </Grid>
                                }
                                <Grid item sm={12} xs={12}>
                                    <Box marginTop="24px" marginBottom="24px">
                                        <Button type="button" variant="contained" color="primary" fullWidth onClick={handleSubmit}>Submit Review</Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
    );
};

export default AppointmentRate;
